import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createHead } from '@vueuse/head'

import App from './App.vue'
import router from './router'
// i18n import removed - will be implemented later
import { log } from './utils/logger'
import { useAuthStore } from './stores/authStore'
import { useCurrency } from './composables/useCurrency'
import { initAnalyticsIfConsented } from './utils/gtag'

import './assets/base.scss'

// Performance monitoring
const startTime = performance.now()

// Create app with performance optimizations
const app = createApp(App)

// Performance optimizations
app.config.performance = true

// Error handling (no analytics in production)
app.config.warnHandler = (msg, instance, trace) => {
  console.warn(msg, instance, trace)
}

app.config.errorHandler = (err: unknown, instance, info) => {
  const errorMessage = err instanceof Error ? err.message : String(err)
  console.error('Global error:', err, instance, info)
}

// Use plugins
app.use(createPinia())
app.use(router)
app.use(createHead()) // ✅ Add head manager for SEO
// i18n plugin removed - will be implemented later

// Google Analytics 4 — no-ops outside the production hostname (so staging and
// local development never report into the production property) and no-ops
// unless the visitor already consented in a previous session. A first-time
// visitor sees CookieConsentBanner.vue, which starts GA itself on accept.
initAnalyticsIfConsented(router)

// URL normalization guard (client safety net)
router.beforeEach((to) => {
  // do not touch static assets routes etc.
  if (to.path.startsWith('/assets') || to.path.startsWith('/images')) return true

  const path = to.path
  // normalize to lowercase
  const lower = path.toLowerCase()
  // remove trailing slash (except root)
  const trimmed = lower !== '/' && lower.endsWith('/') ? lower.slice(0, -1) : lower

  if (trimmed !== to.path) {
    return { path: trimmed, query: to.query, hash: to.hash, replace: true }
  }
  return true
})

// Mount app
app.mount('#app')

// Initialize auth store after app is mounted
const authStore = useAuthStore()
authStore
  .initialize()
  .then(() => {
    log.info('🔐 Auth store initialized')
  })
  .catch((error) => {
    log.error('❌ Failed to initialize auth store:', error)
  })

// Initialize currency system
const { initialize: initializeCurrency } = useCurrency()
initializeCurrency()
  .then(() => {
    log.info('💰 Currency system initialized')
  })
  .catch((error) => {
    log.error('❌ Failed to initialize currency system:', error)
  })

// Track app load time
const loadTime = performance.now() - startTime
// log.info(`🚀 TakeFav App loaded in ${loadTime.toFixed(2)}ms`)
// Force cache bust Sun Aug 17 22:47:00 IDT 2025
